"use client";

import { useTracks } from "@livekit/components-react";
import { RemoteAudioTrack, Track } from "livekit-client";
import { Mic, MicOff, Wind } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import BioCanvas from "./BioCanvas";
import StealthInterface from "./StealthInterface";
import { useZenContext } from "./ZenContext";

interface ZenInterfaceProps {
  isMuted?: boolean;
  onToggleMute?: () => void;
}

const ZenInterface: React.FC<ZenInterfaceProps> = ({ isMuted = false, onToggleMute }) => {
  const { isStealthMode, toggleStealthMode } = useZenContext();
  const [volume, setVolume] = useState(0);
  const volumeRef = useRef(0);

  // Get the agent's audio track (Remote Microphone)
  const tracks = useTracks([Track.Source.Microphone]);
  const agentTrackRef = tracks.find(
    (t) => t.publication.kind === Track.Kind.Audio && !t.participant.isLocal
  );
  const agentTrackSid = agentTrackRef?.publication?.trackSid || null;

  // Audio visualization logic
  useEffect(() => {
    if (isStealthMode || !agentTrackRef?.publication.track) return;

    const track = agentTrackRef.publication.track as RemoteAudioTrack;
    const mediaStream = track.mediaStream;

    if (!mediaStream) return;

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let animationFrameId: number | null = null;

    const initAudio = () => {
      try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        source = audioContext.createMediaStreamSource(mediaStream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateVolume = () => {
          if (!analyser) return;
          analyser.getByteFrequencyData(dataArray);

          // Calculate average volume
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const average = sum / dataArray.length;

          // Normalize to 0-1 range (approximate max 255 but usually lower speech)
          // Boost it a bit for visual impact
          const normalizedVolume = Math.min(1, (average / 128) * 1.5);

          // Avoid re-rendering on every tiny fluctuation/frame
          if (Math.abs(normalizedVolume - volumeRef.current) > 0.02) {
            volumeRef.current = normalizedVolume;
            setVolume(normalizedVolume);
          }
          animationFrameId = requestAnimationFrame(updateVolume);
        };

        updateVolume();
      } catch (error) {
        console.error("Failed to initialize audio analyzer:", error);
      }
    };

    initAudio();

    return () => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      if (source) source.disconnect();
      if (audioContext && audioContext.state !== "closed") audioContext.close();
    };
  }, [isStealthMode, agentTrackSid]);

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
      {/* Main Visualizer Area */}
      <div className="absolute inset-0 z-0">
        {/* Conditionally render BioCanvas or StealthInterface */}
        {isStealthMode ? (
          <StealthInterface volume={volume} />
        ) : (
          <BioCanvas volume={volume} mode="normal" thoughts={[]} variant="zen" />
        )}
      </div>

      {/* Top Controls Layer: Stealth trigger stays away from Stealth footer controls */}
      <div className="absolute top-6 right-6 z-30">
        {/* Stealth Mode Toggle */}
        <button
          onClick={toggleStealthMode}
          className={`p-4 rounded-full backdrop-blur-md transition-all duration-300 ${
            isStealthMode
              ? "bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 ring-1 ring-orange-500/50"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
        >
          <Wind size={24} />
          <span className="sr-only">Toggle Stealth Mode</span>
        </button>
      </div>

      {/* Bottom Controls Layer */}
      <div className="absolute bottom-10 z-20 flex items-center">
        {/* Mute Toggle */}
        <button
          onClick={onToggleMute}
          className={`p-4 rounded-full backdrop-blur-md transition-all duration-300 ${
            isMuted
              ? "bg-red-500/20 text-red-200 hover:bg-red-500/30"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
        >
          {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
        </button>
      </div>
    </div>
  );
};

export default ZenInterface;
